import { useEffect } from "react";
import Reveal from "@/components/Reveal";

const UPDATED = "February 2026";

const CONTENT = {
  privacy: {
    label: "Legal",
    title: "Privacy Policy",
    intro:
      "Track Digital (we, us, our) is committed to protecting your privacy. This policy explains what personal information we collect, how we use it and the rights you have. It applies to this website and to enquiries you make with us.",
    sections: [
      {
        h: "Who we are",
        p: [
          "Track Digital is a boutique digital marketing agency based in the United Kingdom. For any privacy questions, you can contact us at hello@track-digital.com.",
        ],
      },
      {
        h: "Information we collect",
        p: ["We only collect information you choose to give us. This may include:"],
        list: [
          "Your name and email address when you complete the contact form or book a discovery call.",
          "The details of your enquiry or message.",
          "Your email address if you subscribe to our newsletter.",
          "Your answers if you complete the AI Readiness Quiz.",
          "Basic, anonymous usage information to help us understand how the website is used.",
        ],
      },
      {
        h: "How we use your information",
        p: ["We use the information you provide to:"],
        list: [
          "Respond to your enquiry and arrange a discovery call.",
          "Provide the services you have asked about.",
          "Send you our newsletter, where you have subscribed.",
          "Improve our website and the way we communicate.",
        ],
      },
      {
        h: "Legal basis",
        p: [
          "Under UK data protection law, we process your information on the basis of your consent (for example, subscribing to our newsletter) and our legitimate interest in responding to enquiries and running our business. You can withdraw consent at any time.",
        ],
      },
      {
        h: "Sharing your information",
        p: [
          "We do not sell your personal information. We only share it with trusted service providers who help us operate, such as our website hosting and email delivery partners, and only so they can perform their service for us. These providers are required to keep your information secure.",
        ],
      },
      {
        h: "How long we keep it",
        p: [
          "We keep your information only for as long as necessary for the purpose it was collected, or as required to meet legal or accounting obligations. You can ask us to delete your information at any time.",
        ],
      },
      {
        h: "Your rights",
        p: ["Under UK GDPR you have the right to:"],
        list: [
          "Access the personal information we hold about you.",
          "Ask us to correct or update it.",
          "Ask us to delete it.",
          "Object to or restrict how we use it.",
          "Unsubscribe from our marketing at any time.",
        ],
      },
      {
        h: "Cookies",
        p: [
          "This website uses only essential cookies needed for it to function correctly, along with basic, privacy-friendly analytics. We do not use intrusive advertising or third-party tracking cookies.",
        ],
      },
      {
        h: "Contact",
        p: [
          "If you have any questions about this policy or wish to exercise your rights, please email hello@track-digital.com. You also have the right to complain to the Information Commissioner's Office (ICO) at ico.org.uk.",
        ],
      },
    ],
  },
  terms: {
    label: "Legal",
    title: "Terms of Service",
    intro:
      "These terms explain how you may use the Track Digital website. By using this website you agree to these terms. Specific project work is always covered by a separate written proposal or agreement.",
    sections: [
      {
        h: "Using this website",
        p: [
          "You may use this website for lawful purposes only. You agree not to misuse it, attempt to gain unauthorised access, or use it in any way that could damage or disrupt the site or other users.",
        ],
      },
      {
        h: "Our content",
        p: [
          "All content on this website, including text, design, graphics and branding, belongs to Track Digital unless stated otherwise. You may not copy, reproduce or reuse it without our permission.",
        ],
      },
      {
        h: "Enquiries and quotes",
        p: [
          "Information about services and pricing on this website is provided for guidance. Prices marked POA (Price on Application) are tailored to each project. Nothing on this website forms a binding quote or contract. Any work we undertake for you will be confirmed in a separate written proposal.",
        ],
      },
      {
        h: "Third-party links",
        p: [
          "This website may link to other websites we do not control. We are not responsible for their content or their privacy practices, and including a link does not imply endorsement.",
        ],
      },
      {
        h: "Liability",
        p: [
          "We take care to keep this website accurate and available, but we provide it on an as is basis. To the extent permitted by law, we are not liable for any loss arising from your use of, or inability to use, this website.",
        ],
      },
      {
        h: "Changes",
        p: [
          "We may update these terms from time to time. The latest version will always be published on this page.",
        ],
      },
      {
        h: "Contact",
        p: ["If you have any questions about these terms, please email hello@track-digital.com."],
      },
    ],
  },
};

export default function Legal({ doc }) {
  const data = CONTENT[doc];

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${data.title} | Track Digital`;
  }, [data.title]);

  return (
    <main data-testid={`legal-${doc}`}>
      <section className="pt-36 md:pt-44 pb-12 md:pb-16 border-b border-[#eee]">
        <div className="td-container max-w-3xl">
          <Reveal>
            <span className="td-label block mb-6">{data.label}</span>
            <h1 className="text-4xl sm:text-5xl font-semibold leading-[1.05]">{data.title}</h1>
            <p className="text-[#8a8a8a] mt-5">Last updated: {UPDATED}</p>
            <p className="td-measure text-lg text-[#4a4a4a] leading-relaxed mt-8">{data.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="td-container max-w-3xl">
          {data.sections.map((s) => (
            <Reveal key={s.h} className="mb-12">
              <h2 className="font-head text-2xl font-semibold text-black mb-4">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="text-[#4a4a4a] text-lg leading-relaxed mb-4">{para}</p>
              ))}
              {s.list && (
                <ul className="list-disc pl-6 space-y-2 text-[#4a4a4a] text-lg leading-relaxed">
                  {s.list.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
            </Reveal>
          ))}
          <p className="text-[#8a8a8a] text-sm mt-8">
            This document is provided as a clear starting point. We recommend reviewing it with a legal
            adviser before relying on it.
          </p>
        </div>
      </section>
    </main>
  );
}
